import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';

@Component({
    selector: 'app-map-sidenav-bid-item-wrapper',
    templateUrl: './map-sidenav-bid-item-wrapper.component.html',
    styleUrls: ['./map-sidenav-bid-item-wrapper.component.scss'],
    standalone: false
})
export class MapSidenavBidItemWrapperComponent implements OnInit, OnChanges {
  @Input() data: any; // Marker data from map
  @Input() highlightLineItemId: string | null = null;
  @Input() highlightFeatureIndex: number | null = null;

  // Transformed data for multiline-bid-item-details
  bidItemData: any = null;

  ngOnInit() {
    this.transformDataForBidItemComponent();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-transform data whenever the input or highlight properties change
    if ((changes['data'] || changes['highlightLineItemId'] || changes['highlightFeatureIndex']) && !changes['data']?.firstChange) {
      this.transformDataForBidItemComponent();
    }
  }

  /**
   * Transform map marker data to format expected by bid-item-details-wrapper
   */
  private transformDataForBidItemComponent(): void {
    if (!this.data) {
      return;
    }

    const sources = this.data.sources || this.data.children || [];
    const bidItemFields = this.data.bid_item?.fields || [];

    // Create field_values for the lineItem (needed by form builder)
    const fieldValues = {};
    bidItemFields.forEach(field => {
      fieldValues[field.id] = {
        id: field.id,
        value: null
      };

      // For single source, use the first source's fields
      if (sources.length === 1) {
        sources[0].source_fields?.forEach(sourceField => {
          for (const key in sourceField) {
            if (field.name === key) {
              fieldValues[field.id] = {
                id: field.id,
                value: sourceField[key]
              };
            }
          }
        });
      }
    });

    // Create sourcesSection (required by multiline-bid-item-details component)
    let sourcesSection = null;
    if (sources.length > 0) {
      const sourcesList = sources.map(source => {
        // Transform source_fields to fieldValues format for each source
        const sourceFieldValues = {};
        bidItemFields.forEach(field => {
          sourceFieldValues[field.id] = {
            id: field.id,
            value: null
          };

          // Map source_fields to field values
          source.source_fields?.forEach(sourceField => {
            for (const key in sourceField) {
              if (field.name === key) {
                sourceFieldValues[field.id] = {
                  id: field.id,
                  value: sourceField[key]
                };
              }
            }
          });
        });

        return {
          ...source,
          fieldValues: Object.values(sourceFieldValues)
        };
      });

      // Create heading and subheading
      sourcesSection = {
        heading: this.data.created_at || new Date().toISOString(),
        subheading: sources.length > 1
          ? `${sources.length} Inspectors`
          : `${sources[0]?.source_inspector || 'Unknown'} (${sources[0]?.source_name || 'N/A'})`,
        sourcesList
      };
    }

    // Add fileType to pictures based on mime type
    const picturesWithFileType = (this.data.pictures || []).map(pic => ({
      ...pic,
      fileType: this.getFileTypeFromMime(pic.mime)
    }));

    const videosWithFileType = (this.data.videos || []).map(vid => ({
      ...vid,
      fileType: 'video'
    }));

    // For consolidated markers, use allLineItems if available (multiple line items grouped)
    // Otherwise, create a single lineItem from marker data
    let transformedLineItems = [];

    if (this.data.allLineItems && this.data.allLineItems.length > 0) {
      // Consolidated marker - transform each line item from allLineItems
      transformedLineItems = this.data.allLineItems.map(lineItem => {
        // Add fileType to pictures for this line item
        const lineItemPictures = (lineItem.pictures || []).map(pic => ({
          ...pic,
          fileType: this.getFileTypeFromMime(pic.mime)
        }));
        const lineItemVideos = (lineItem.videos || []).map(vid => ({
          ...vid,
          fileType: 'video'
        }));

        // Create sourcesSection for this line item
        const lineItemSources = lineItem.sources || lineItem.children || [];
        let lineItemSourcesSection = null;
        if (lineItemSources.length > 0) {
          const sourcesList = lineItemSources.map(source => {
            const sourceFieldValues = {};
            bidItemFields.forEach(field => {
              sourceFieldValues[field.id] = {
                id: field.id,
                value: null
              };
              source.source_fields?.forEach(sourceField => {
                for (const key in sourceField) {
                  if (field.name === key) {
                    sourceFieldValues[field.id] = {
                      id: field.id,
                      value: sourceField[key]
                    };
                  }
                }
              });
            });
            return {
              ...source,
              fieldValues: Object.values(sourceFieldValues)
            };
          });

          lineItemSourcesSection = {
            heading: lineItem.created_at || new Date().toISOString(),
            subheading: lineItemSources.length > 1
              ? `${lineItemSources.length} Inspectors`
              : `${lineItemSources[0]?.source_inspector || 'Unknown'} (${lineItemSources[0]?.source_name || 'N/A'})`,
            sourcesList
          };
        }

        return {
          id: lineItem.id,
          quantity: lineItem.quantity || 0,
          comment: lineItem.comment || null,
          latitude: lineItem.latitude || null,
          longitude: lineItem.longitude || null,
          start_of_work: lineItem.start_of_work || false,
          station_break: lineItem.station_break || false,
          pay_app_id: lineItem.pay_app_id || null,
          rollup_quantity_manual: lineItem.rollup_quantity_manual || null,
          station_range: lineItem.station_range || null,
          location: lineItem.location || null,
          geometry: lineItem.geometry || null,
          map_feature_link: lineItem.map_feature_link ?? null,
          pictures: lineItemPictures,
          videos: lineItemVideos,
          sources: lineItemSources,
          sourcesSection: lineItemSourcesSection,
          station: lineItem.station || null,
          heading: lineItem.heading || null,
          bid_item: this.data.bid_item,
          created_at: lineItem.created_at || new Date().toISOString(),
          field_values: lineItem.field_values || []
        };
      });
    } else {
      // Single marker - use original logic
      transformedLineItems = [{
        id: this.data.id,
        quantity: this.data.quantity || 0,
        comment: this.data.comment || null,
        latitude: this.data.latitude || null,
        longitude: this.data.longitude || null,
        start_of_work: this.data.start_of_work || false,
        station_break: this.data.station_break || false,
        pay_app_id: this.data.pay_app_id || null,
        rollup_quantity_manual: this.data.rollup_quantity_manual || null,
        station_range: this.data.station_range || null,
        location: this.data.location || null,
        geometry: this.data.geometry || null,
        map_feature_link: this.data.map_feature_link ?? null,
        pictures: picturesWithFileType,
        videos: videosWithFileType,
        sources,
        sourcesSection,
        station: this.data.station || null,
        heading: this.data.heading || null,
        bid_item: this.data.bid_item,
        created_at: this.data.created_at || new Date().toISOString(),
        field_values: Object.values(fieldValues)
      }];
    }

    // Transform marker data to match daily report format
    this.bidItemData = {
      bidItem: this.data.bid_item,
      siteName: this.data.siteName,
      reportDate: this.data.reportDate,
      lineItems: transformedLineItems,
      clickId: this.highlightLineItemId || this.data.id,
      highlightFeatureIndex: this.highlightFeatureIndex,
      isAnyLinePaid: false // Map view is read-only
    };

  }

  /**
   * Convert MIME type to fileType expected by pictures-section component
   */
  private getFileTypeFromMime(mime: string): string {
    if (!mime) return 'picture'; // Default to picture if no mime

    if (mime.startsWith('image/')) {
      return 'picture';
    } else if (mime.startsWith('video/')) {
      return 'video';
    } else if (mime.startsWith('application/') || mime.startsWith('text/')) {
      return 'document';
    }

    return 'picture'; // Default fallback
  }
}
